'use client';

import { useEffect, useState } from 'react';
import { PipelineDoc } from './PipelineDoc';

// WHICH PROJECT — and how many at once.
//
// Boss: "I will manually launch it with one of the projects, also I should be able
// to launch 2 projects at the same time."
//
// So this is not a single-select. Boss picks a project to inspect, and a running
// project keeps running whether or not he is looking at it — the run lives in the
// filesystem lock, not in this component. The strip shows every project currently
// walking, so two builds are visible even though only one document is open.

type Proj = { slug: string; name: string; path: string };
// Which Claude account a project bills. Resolved by the GATEWAY (the same
// accountForPath logic a real run uses) — never guessed here. `blocked` means
// a strict route names an account whose auth.json is missing: the run would
// REFUSE to start, so the UI must show that before Boss hits Start.
type Acct = { account: string | null; blocked?: boolean; error?: string };

export function AccountBadge({ acct }: { acct: Acct | undefined }) {
  if (!acct) return null;
  if (acct.blocked) return (
    <span title={acct.error || 'account auth missing'}
          className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 ring-1 ring-red-500/30">
      ⚠ account missing
    </span>
  );
  if (!acct.account) return null;
  const main = acct.account === 'main';
  return (
    <span title={main ? 'bills the main Claude account' : `bills the dedicated “${acct.account}” Claude account`}
          className={`text-[10px] px-1.5 py-0.5 rounded ring-1 ${
            main ? 'bg-white/5 text-gray-500 ring-white/10'
                 : 'bg-violet-500/15 text-violet-300 ring-violet-500/30'}`}>
      ⚡ {acct.account}
    </span>
  );
}

// Self-fetching badge for surfaces that only have a path (e.g. the Run tab's
// project selector — the actual LAUNCH surface, which must show the account
// BEFORE Boss hits Start).
export function ProjectAccountBadge({ path }: { path: string }) {
  const [acct, setAcct] = useState<Acct | undefined>();
  useEffect(() => {
    let stop = false;
    setAcct(undefined); // path changed → stale badge is a wrong badge
    fetch(`/api/pipeline/account?path=${encodeURIComponent(path)}`)
      .then(r => r.json())
      .then(a => { if (!stop) setAcct({ account: a?.account ?? null, blocked: !!a?.blocked, error: a?.error }); })
      .catch(() => { /* unknown stays unknown */ });
    return () => { stop = true; };
  }, [path]);
  return <AccountBadge acct={acct} />;
}

// THE FACTORY-WIDE STRIP — every project with a live run, its pid and its
// billing account, regardless of which project is open below. Self-contained
// (own project fetch + lock poll) so the merged Run tab shows the whole
// factory even though only one project's document is open. Truth comes from
// the filesystem lock via /api/pipeline/run, so a run started by another tab
// — or another human — is just as visible.
// Live run-loop state per project slug: 'running' | 'waiting'.
// Polls /api/pipeline/loop (the engine the Run tab shows) — the strip used to
// poll the legacy /api/pipeline/run lock, so real runs were invisible in it
// (Boss, 2026-09-01: "I should be able to see which ones are working right now").
// FREEZE SCAR (2026-09-01): the first version fetched /api/pipeline/loop per
// project — N python subprocess spawns per mount per tick, unguarded against
// overlap. Under load the ticks stacked and starved the app until nothing was
// clickable. Now: ONE fetch to /api/pipeline/live (reads run.json directly,
// zero subprocesses) and a tick can never start while the previous is in flight.
export function useLiveRuns(projects: Proj[]) {
  const [live, setLive] = useState<Record<string, 'running' | 'waiting'>>({});
  useEffect(() => {
    if (!projects.length) return;
    let stop = false, inFlight = false;
    const tick = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const d = await fetch('/api/pipeline/live').then(r => r.json());
        if (!stop && d?.ok) {
          const found: Record<string, 'running' | 'waiting'> = {};
          for (const p of projects) {
            const s = d.live?.[p.slug]?.state;
            if (s === 'running' || s === 'waiting') found[p.slug] = s;
          }
          setLive(found);
        }
      } catch { /* gateway of truth unreachable — keep last state */ }
      inFlight = false;
    };
    tick();
    const t = setInterval(tick, 5000);
    return () => { stop = true; clearInterval(t); };
  }, [projects]);
  return live;
}

export function WalkingStrip() {
  const [projects, setProjects] = useState<Proj[]>([]);
  const [accounts, setAccounts] = useState<Record<string, Acct>>({});

  useEffect(() => {
    fetch('/api/projects').then(r => r.json()).then(d => {
      setProjects((Array.isArray(d) ? d : d.projects || [])
        .filter((p: any) => p?.path && p?.slug)
        .map((p: any) => ({ slug: p.slug, name: p.name || p.slug, path: p.path })));
    }).catch(() => setProjects([]));
  }, []);

  const live = useLiveRuns(projects);

  // account per RUNNING project only — the strip is small, keep the calls small
  useEffect(() => {
    const need = Object.keys(live).filter(s => !accounts[s]);
    if (!need.length) return;
    let stop = false;
    (async () => {
      const found: Record<string, Acct> = {};
      await Promise.all(need.map(async slug => {
        const p = projects.find(x => x.slug === slug);
        if (!p) return;
        try {
          const a = await fetch(`/api/pipeline/account?path=${encodeURIComponent(p.path)}`)
            .then(r => r.json());
          found[slug] = { account: a?.account ?? null, blocked: !!a?.blocked, error: a?.error };
        } catch { /* unknown stays unknown */ }
      }));
      if (!stop && Object.keys(found).length) setAccounts(prev => ({ ...prev, ...found }));
    })();
    return () => { stop = true; };
  }, [live, projects]); // eslint-disable-line react-hooks/exhaustive-deps

  const liveSlugs = Object.keys(live);
  if (!liveSlugs.length) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap rounded-lg bg-emerald-500/10
                    ring-1 ring-emerald-500/25 px-3 py-2 mb-4">
      <span className="text-[11px] uppercase tracking-wider text-emerald-400">
        {liveSlugs.length} walking now
      </span>
      {liveSlugs.map(s => (
        <span key={s} className={`flex items-center gap-1.5 text-[11px] ${
          live[s] === 'waiting' ? 'text-amber-200' : 'text-emerald-200'}`}>
          <span className={`h-1.5 w-1.5 rounded-full animate-pulse ${
            live[s] === 'waiting' ? 'bg-amber-400' : 'bg-emerald-400'}`} />
          {/* Display NAME, not the disk slug. The slug is an internal
              identifier (paths, ports, git remote) and deliberately differs
              from the brand — e.g. `growth-bottleneck` is "Executive Coaching
              Advisory". The picker below already renders p.name; this strip
              was the one place still leaking the raw slug. */}
          {projects.find(p => p.slug === s)?.name || s}
          {live[s] === 'waiting' ? ' · waiting on you' : ''}
          <AccountBadge acct={accounts[s]} />
        </span>
      ))}
    </div>
  );
}

export function PipelinePicker() {
  const [projects, setProjects] = useState<Proj[]>([]);
  const [active, setActive] = useState<Proj | null>(null);
  const [live, setLive] = useState<Record<string, string>>({});
  const [accounts, setAccounts] = useState<Record<string, Acct>>({});

  useEffect(() => {
    fetch('/api/projects').then(r => r.json()).then(d => {
      const rows: Proj[] = (Array.isArray(d) ? d : d.projects || [])
        .filter((p: any) => p?.path && p?.slug)
        .map((p: any) => ({ slug: p.slug, name: p.name || p.slug, path: p.path }));
      setProjects(rows);
      // Share the SAME saved project as the Run/Brief/Seeds tabs. Read storage
      // directly: usePersistedState hydrates in an effect, so a hook value read
      // here would still be '' and fall through to rows[0] — the exact race
      // that made this default to graffitiremoval-ca every time.
      let saved = '';
      try { saved = JSON.parse(localStorage.getItem('beast:factory:project') || '""'); }
      catch { saved = ''; }
      setActive(prev => prev ?? rows.find((r: Proj) => r.slug === saved) ?? rows[0] ?? null);
    }).catch(() => setProjects([]));
  }, []);

  // Resolve each project's billing account once per project list. The answer
  // comes from the gateway's real router, so the badge cannot drift from what
  // a run would actually bill.
  useEffect(() => {
    if (!projects.length) return;
    let stop = false;
    (async () => {
      const found: Record<string, Acct> = {};
      await Promise.all(projects.map(async p => {
        try {
          const a = await fetch(`/api/pipeline/account?path=${encodeURIComponent(p.path)}`)
            .then(r => r.json());
          found[p.slug] = { account: a?.account ?? null, blocked: !!a?.blocked, error: a?.error };
        } catch { /* unknown stays unknown — no badge beats a wrong badge */ }
      }));
      if (!stop) setAccounts(found);
    })();
    return () => { stop = true; };
  }, [projects]);

  // Poll every project for a live lock. This is how two parallel builds stay
  // visible: the answer comes from the filesystem, so it is true even for a run
  // this browser tab never started.
  useEffect(() => {
    if (!projects.length) return;
    let stop = false;
    const tick = async () => {
      const found: Record<string, string> = {};
      await Promise.all(projects.map(async p => {
        try {
          const s = await fetch(
            `/api/pipeline/run?path=${encodeURIComponent(p.path)}&stage=0`,
          ).then(r => r.json());
          if (s?.running) found[p.slug] = s.pid || '?';
        } catch { /* a project that cannot be polled is simply not running */ }
      }));
      if (!stop) setLive(found);
    };
    tick();
    const t = setInterval(tick, 4000);
    return () => { stop = true; clearInterval(t); };
  }, [projects]);

  const liveSlugs = Object.keys(live);

  return (
    <div className="flex flex-col gap-3">
      {liveSlugs.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap rounded-lg bg-emerald-500/10
                        ring-1 ring-emerald-500/25 px-3 py-2">
          <span className="text-[11px] uppercase tracking-wider text-emerald-400">
            {liveSlugs.length} walking now
          </span>
          {liveSlugs.map(s => (
            <span key={s} className="flex items-center gap-1.5 text-[11px] text-emerald-200">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {projects.find(p => p.slug === s)?.name || s} · pid {live[s]}
              <AccountBadge acct={accounts[s]} />
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-wider text-gray-500">Project</span>
        <select
          value={active?.slug || ''}
          onChange={e => {
            const p = projects.find(x => x.slug === e.target.value) || null;
            setActive(p);
            // persist so every other tab follows this choice too
            try { localStorage.setItem('beast:factory:project', JSON.stringify(p?.slug || '')); } catch {}
          }}
          className="bg-white/5 text-gray-200 text-xs rounded-lg px-3 py-1.5
                     ring-1 ring-white/10 focus:outline-none focus:ring-cyan-500/40">
          {projects.length === 0 && <option value="">no projects found</option>}
          {projects.map(p => (
            <option key={p.slug} value={p.slug} className="bg-[#0b0d12]">
              {p.name}{live[p.slug] ? '  ● running' : ''}
            </option>
          ))}
        </select>
        {active && <AccountBadge acct={accounts[active.slug]} />}
        {active && <span className="text-[11px] text-gray-600 font-mono">{active.path}</span>}
      </div>

      {active
        ? <PipelineDoc key={active.slug} slug={active.slug} path={active.path} />
        : <div className="text-xs text-gray-600">Select a project.</div>}
    </div>
  );
}
